/**
 * CL-337I: Stable projectFileShares document IDs + matrix column keys.
 * - UID shares: {fileId}__{firebaseAuthUid}
 * - Pre-signup email shares: {fileId}__{emailLower} (must match rules + queries using normalized email)
 *
 * Deployed drawing file-read rules only exists() Auth-UID or email-keyed docs:
 *   projectFileShares/{fileId}__{request.auth.uid}
 *   projectFileShares/{fileId}__{request.auth.token.email.lower()}
 * Roster `uid` is often a CRM users/{id} and is NOT request.auth.uid. Writers must
 * not key drawing View shares to the CRM id when a linked Auth UID exists.
 *
 * Canonical Portal Access Contract: docs/PORTAL_ACCESS_CONTRACT.md
 * CRM-keyed leftovers are COMPATIBILITY-ONLY (rewrite via Align); never the preferred write target.
 */

export function normalizeShareEmail(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

export function shareDocIdForUid(fileId, uid) {
  const fid = String(fileId ?? "").trim();
  const u = String(uid ?? "").trim();
  return `${fid}__${u}`;
}

/** Document id for email-keyed share; emailLower must already be normalized. */
export function shareDocIdForEmail(fileId, emailLower) {
  const fid = String(fileId ?? "").trim();
  const em = normalizeShareEmail(emailLower);
  return em ? `${fid}__${em}` : "";
}

function uniqueIds(ids) {
  const seen = new Set();
  const out = [];
  (Array.isArray(ids) ? ids : []).forEach((id) => {
    const v = String(id ?? "").trim();
    if (!v || seen.has(v)) return;
    seen.add(v);
    out.push(v);
  });
  return out;
}

function isLiveProjectFile(file) {
  if (!file || typeof file !== "object") return false;
  const st = String(file.status ?? "active").trim().toLowerCase();
  if (st === "deleted" || file.deleted === true) return false;
  return Boolean(String(file.id ?? file.fileId ?? "").trim());
}

/**
 * Stamp linked Firebase Auth UID onto a collaborator roster row.
 * Admin matrix / Prepare package must not write CRM-keyed shares when users/{crm}.authUid exists.
 */
export function enrichCollaboratorWithLinkedAuthUid(row = {}, linkedAuthUid = "") {
  const authUid = String(linkedAuthUid ?? row?.authUid ?? "").trim();
  if (!row || typeof row !== "object") return row;
  if (!authUid) return { ...row, authUid: String(row?.authUid ?? "").trim() || null };
  return { ...row, authUid };
}

/**
 * Enrich roster rows from users/{id} docs (CRM id → authUid).
 * usersById values may be raw Firestore user docs or { id, authUid }.
 */
export function enrichCollaboratorsWithLinkedAuthUids(collaborators = [], usersById = {}) {
  const map = usersById && typeof usersById === "object" ? usersById : {};
  return (Array.isArray(collaborators) ? collaborators : []).map((row) => {
    const rosterUid = String(row?.uid ?? "").trim();
    const emailLower = normalizeShareEmail(row?.email ?? row?.emailLower ?? "");
    const fromRoster = map[rosterUid] || null;
    const fromEmail =
      emailLower &&
      Object.values(map).find(
        (u) => normalizeShareEmail(u?.emailLower || u?.email) === emailLower
      );
    const userDoc = fromRoster || fromEmail || null;
    const linked =
      String(userDoc?.authUid ?? "").trim() ||
      // Auth-canonical users/{authUid} docs use the doc id as Auth UID.
      (userDoc &&
      rosterUid &&
      String(userDoc?.id || "").trim() === rosterUid &&
      String(userDoc?.portalRecordKind || "").trim() === "auth_runtime_profile"
        ? rosterUid
        : "") ||
      String(row?.authUid ?? "").trim();
    return enrichCollaboratorWithLinkedAuthUid(row, linked);
  });
}

/**
 * Upsert Auth-UID-keyed projectAssignments so drawing GATE 1 exists() can succeed.
 * Copies role/permissions from the enriched collaborator (or a source email/CRM assignment).
 * Does not broaden drawing View — shares remain separate.
 */
export function planLinkedAuthAssignmentUpserts({
  projectId = "",
  collaborators = [],
  existingAssignments = [],
} = {}) {
  const pid = String(projectId ?? "").trim();
  if (!pid) return [];
  const existingById = new Map();
  (Array.isArray(existingAssignments) ? existingAssignments : []).forEach((row) => {
    const id = String(row?.id ?? row?.__docId ?? "").trim();
    if (id) existingById.set(id, row);
  });

  const ops = [];
  const seen = new Set();
  (Array.isArray(collaborators) ? collaborators : []).forEach((row) => {
    const authUid = String(row?.authUid ?? "").trim();
    const crmUid = String(row?.uid ?? "").trim();
    const emailLower = normalizeShareEmail(row?.email ?? row?.emailLower ?? "");
    if (!authUid) return;
    const refId = `${pid}__${authUid}`;
    if (seen.has(refId)) return;
    seen.add(refId);

    const existing = existingById.get(refId) || null;
    const source =
      (crmUid &&
        [...existingById.values()].find(
          (a) => String(a?.userId ?? "").trim() === crmUid && String(a?.projectId ?? "").trim() === pid
        )) ||
      (emailLower &&
        [...existingById.values()].find(
          (a) =>
            normalizeShareEmail(a?.emailLower || a?.email) === emailLower &&
            String(a?.projectId ?? "").trim() === pid
        )) ||
      null;

    const permissions =
      (existing?.permissions && typeof existing.permissions === "object"
        ? existing.permissions
        : null) ||
      (source?.permissions && typeof source.permissions === "object" ? source.permissions : null) ||
      (row?.permissions && typeof row.permissions === "object" ? row.permissions : {}) ||
      {};

    const alreadyLinked =
      existing &&
      String(existing.userId ?? "").trim() === authUid &&
      String(existing.status || "active").toLowerCase() === "active" &&
      existing.permissions?.canReviewDrawings === true;

    if (alreadyLinked && existing?.permissions?.canReviewDrawings === permissions?.canReviewDrawings) {
      // Still refresh if role/scope drifted from roster.
      const sameRole = String(existing.role || "") === String(row?.role || existing.role || "");
      const sameScope =
        String(existing.accessScope || "full_project") ===
        String(row?.accessScope || existing.accessScope || "full_project");
      if (sameRole && sameScope) return;
    }

    ops.push({
      kind: "set",
      refId,
      data: {
        projectId: pid,
        userId: authUid,
        email: row?.email || source?.email || null,
        emailLower: emailLower || source?.emailLower || null,
        displayName: row?.name || source?.displayName || null,
        company: row?.company || source?.company || null,
        role: String(row?.role || source?.role || "engineer").trim() || "engineer",
        accessScope:
          String(row?.accessScope || source?.accessScope || "full_project").trim() || "full_project",
        status: "active",
        permissions,
        portalEnabled: true,
        linkedFromAssignmentId: source?.id || source?.__docId || null,
      },
    });
  });
  return ops;
}

/**
 * Share writes that portals can actually read.
 * Prefer linked Firebase Auth UID; email-key when unlinked; never prefer CRM id over Auth.
 */
export function resolveDrawingShareWriteTarget(row = {}) {
  const rosterUid = String(row?.uid ?? row?.userId ?? "").trim();
  const authUid = String(row?.authUid ?? "").trim();
  const emailLower = normalizeShareEmail(row?.email ?? row?.emailLower ?? row?.sharedWithEmail ?? "");

  if (authUid) {
    return {
      kind: "uid",
      uid: authUid,
      emailLower,
      rosterUid,
      authUid,
      legacyUids: rosterUid && rosterUid !== authUid ? [rosterUid] : [],
    };
  }
  if (rosterUid) {
    return {
      kind: "uid",
      uid: rosterUid,
      emailLower,
      rosterUid,
      authUid: "",
      legacyUids: [],
    };
  }
  if (emailLower) {
    return {
      kind: "email",
      uid: "",
      emailLower,
      rosterUid: "",
      authUid: "",
      legacyUids: [],
    };
  }
  return null;
}

/** Doc ids leftover from a CRM-keyed (or email-keyed) write after moving to the canonical target. */
export function leftoverShareDocIdsForTarget(fileId, target) {
  const fid = String(fileId ?? "").trim();
  if (!fid || !target) return [];
  const ids = [];
  (Array.isArray(target.legacyUids) ? target.legacyUids : []).forEach((u) => {
    const id = shareDocIdForUid(fid, u);
    if (id && id !== `${fid}__`) ids.push(id);
  });
  if (target.kind === "uid" && target.authUid && target.legacyUids.length && target.emailLower) {
    const emailId = shareDocIdForEmail(fid, target.emailLower);
    if (emailId) ids.push(emailId);
  }
  const writeId =
    target.kind === "email"
      ? shareDocIdForEmail(fid, target.emailLower)
      : shareDocIdForUid(fid, target.uid);
  return uniqueIds(ids).filter((id) => id && id !== writeId);
}

/**
 * Canonical write + leftover deletes for one file × collaborator.
 * leftoverIds must be deleted after a successful Auth/email write so rules exists() hits the live doc.
 */
export function buildDrawingShareWritePlan(fileId, row = {}) {
  const fid = String(fileId ?? "").trim();
  const target = resolveDrawingShareWriteTarget(row);
  if (!fid || !target) return null;
  const writeId =
    target.kind === "email"
      ? shareDocIdForEmail(fid, target.emailLower)
      : shareDocIdForUid(fid, target.uid);
  if (!writeId) return null;
  const leftoverIds = leftoverShareDocIdsForTarget(fid, target);
  return {
    writeId,
    leftoverIds,
    unshareIds: uniqueIds([writeId, ...leftoverIds]),
    sharedWithUid: target.kind === "uid" ? target.uid : null,
    sharedWithEmail: target.emailLower || null,
    target,
  };
}

/**
 * Doc ids deployed drawing-read rules can exists() for this signed-in viewer.
 * CRM-keyed ids are intentionally absent — that is the live Mark BT9er failure.
 */
export function drawingShareDocIdsReadableByAuthRules(fileId, { authUid = "", authEmail = "" } = {}) {
  const fid = String(fileId ?? "").trim();
  if (!fid) return [];
  const ids = [];
  const uid = String(authUid ?? "").trim();
  const em = normalizeShareEmail(authEmail);
  if (uid) ids.push(shareDocIdForUid(fid, uid));
  if (em) ids.push(shareDocIdForEmail(fid, em));
  return uniqueIds(ids);
}

export function drawingShareDocIdReadableByAuthRules(docId, fileId, identity = {}) {
  const id = String(docId ?? "").trim();
  if (!id) return false;
  return drawingShareDocIdsReadableByAuthRules(fileId, identity).includes(id);
}

/**
 * Explicit Admin rewrite: copy live CRM-keyed View shares onto the linked Auth UID, then
 * delete the CRM-keyed leftover. Skips deleted files. Does not grant View that was not already set.
 * Does not run unless the caller writes the returned ops.
 */
export function planLinkedAuthShareRewrites({
  collaborators = [],
  shares = [],
  projectFiles = [],
  projectId = "",
} = {}) {
  const pid = String(projectId ?? "").trim();
  const liveIds = new Set(
    (Array.isArray(projectFiles) ? projectFiles : [])
      .filter((f) => isLiveProjectFile(f))
      .map((f) => String(f?.id ?? f?.fileId ?? "").trim())
      .filter(Boolean)
  );
  const shareByDocId = new Map();
  (Array.isArray(shares) ? shares : []).forEach((share) => {
    const id = String(share?.id ?? share?.__docId ?? "").trim();
    if (id) shareByDocId.set(id, share);
  });

  const ops = [];
  const seen = new Set();
  const push = (op) => {
    const key = `${op.kind}:${op.refId}`;
    if (!op?.refId || seen.has(key)) return;
    seen.add(key);
    ops.push(op);
  };

  (Array.isArray(collaborators) ? collaborators : []).forEach((row) => {
    const target = resolveDrawingShareWriteTarget(row);
    if (!target?.authUid || !Array.isArray(target.legacyUids) || !target.legacyUids.length) return;
    const writeUid = target.uid;
    (Array.isArray(shares) ? shares : []).forEach((share) => {
      const fileId = String(share?.fileId ?? "").trim();
      if (!fileId || !liveIds.has(fileId)) return;
      const sharePid = String(share?.projectId ?? "").trim();
      if (pid && sharePid && sharePid !== pid) return;
      const visibility = String(share?.visibility ?? "shared").trim().toLowerCase();
      if (visibility !== "shared") return;
      if (share?.canView === false || share?.shared === false) return;
      const suid = String(share?.sharedWithUid ?? "").trim();
      const docId = String(share?.id ?? share?.__docId ?? "").trim();
      const isLegacy =
        target.legacyUids.includes(suid) ||
        target.legacyUids.some((u) => docId && docId === shareDocIdForUid(fileId, u));
      if (!isLegacy) return;
      const newId = shareDocIdForUid(fileId, writeUid);
      const oldId = docId || shareDocIdForUid(fileId, suid || target.legacyUids[0]);
      if (!newId || !oldId || newId === oldId) return;
      if (!shareByDocId.has(newId)) {
        const data = {
          projectId: pid || sharePid,
          fileId,
          fileName: share.fileName || "",
          sharedWithUid: writeUid,
          sharedWithEmail: target.emailLower || share.sharedWithEmail || null,
          sharedWithEmailLower: target.emailLower || null,
          role: share.role || "reviewer",
          visibility: "shared",
        };
        if (share.canView !== undefined) data.canView = share.canView;
        if (share.canComment !== undefined) data.canComment = share.canComment;
        if (share.canUploadRevision !== undefined) data.canUploadRevision = share.canUploadRevision;
        push({ kind: "set", refId: newId, fileId, data });
      }
      push({ kind: "delete", refId: oldId, fileId });
    });
  });
  return ops;
}

/** UI / matrix column key from a collaborator roster row */
export function collaboratorShareColKey(row) {
  const uid = String(row?.uid ?? "").trim();
  if (uid) return `u:${uid}`;
  const em = normalizeShareEmail(row?.email ?? row?.emailLower ?? "");
  return em ? `e:${em}` : "";
}

/**
 * Column descriptor for matching roster rows to share rows (same shape as bulk matrix colDefs).
 * `uid` stays the roster/CRM key so columns remain stable; writes use writeUid / buildDrawingShareWritePlan.
 */
export function collaboratorRowToColDef(row) {
  const uid = String(row?.uid ?? "").trim();
  const emailLower = normalizeShareEmail(row?.email ?? row?.emailLower ?? "");
  const authUid = String(row?.authUid ?? "").trim();
  const target = resolveDrawingShareWriteTarget(row);
  if (uid) {
    return {
      colKey: `u:${uid}`,
      kind: "uid",
      uid,
      writeUid: target?.uid || "",
      authUid,
      emailLower: emailLower || "",
      row,
    };
  }
  if (emailLower) {
    return {
      colKey: `e:${emailLower}`,
      kind: "email",
      uid: "",
      writeUid: "",
      authUid,
      emailLower,
      row,
    };
  }
  return null;
}

function uidMatchList(colDef) {
  return uniqueIds([
    colDef?.uid,
    colDef?.writeUid,
    colDef?.authUid,
    colDef?.row?.uid,
    colDef?.row?.authUid,
  ]);
}

/**
 * Whether a projectFileShares payload belongs to a matrix/drawer column.
 * - UID columns: sharedWithUid matches roster uid, linked Auth uid, OR legacy/canonical doc ids.
 * - Email columns: same email and (no uid on share OR email-keyed doc id OR uid matches roster row for same person).
 */
export function shareRowMatchesCollaboratorColumn(share, colDef, fileId) {
  if (!colDef || !share) return false;
  const fid = String(fileId ?? "").trim();
  if (fid && String(share.fileId ?? "").trim() !== fid) return false;
  const suid = String(share.sharedWithUid ?? "").trim();
  const sem = normalizeShareEmail(share.sharedWithEmail ?? share.sharedWithEmailLower ?? "");
  const docId = String(share.__docId ?? share.id ?? "");

  if (colDef.kind === "uid") {
    const matchUids = uidMatchList(colDef);
    if (!matchUids.length && !colDef.uid) return false;
    if (suid && matchUids.includes(suid)) return true;
    if (fid && docId) {
      for (const u of matchUids) {
        if (docId === shareDocIdForUid(fid, u)) return true;
      }
    }
    const em = colDef.emailLower || normalizeShareEmail(colDef.row?.email ?? colDef.row?.emailLower ?? "");
    if (em && sem === em) {
      if (!suid || matchUids.includes(suid)) return true;
      if (fid && docId && docId === shareDocIdForEmail(fid, em)) return true;
    }
    return false;
  }
  if (colDef.kind === "email") {
    if (!colDef.emailLower || sem !== colDef.emailLower) return false;
    if (!suid) return true;
    if (fid && docId && docId === shareDocIdForEmail(fid, colDef.emailLower)) return true;
    const rosterUid = String(colDef.row?.uid ?? "").trim();
    const authUid = String(colDef.row?.authUid ?? "").trim();
    if (rosterUid && suid === rosterUid) return true;
    if (authUid && suid === authUid) return true;
    return false;
  }
  return false;
}
